import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BlockRendererV2Component } from '../block-renderer-v2/block-renderer-v2.component';
import { ReviewPanelV2Component } from '../review/review-panel-v2/review-panel-v2.component';
import { SubjectComponentV2 } from '../../blocks-v2/subject-v2/subject-v2.component';
import { PreheaderComponentV2 } from '../../blocks-v2/preheader-v2/preheader-v2.component';
import { IBlockForSelect } from '../../../../utils/blocks-helper';

/**
 * Region 6 of the mockup: the right-hand panel with Block Properties / Comments
 * tabs. Block Properties hosts the currently-selected block's existing, unmodified
 * component instance (isPreview=false - full form, live-bound to the same `block`
 * object rendered in the sidebar/preview, so edits here update everywhere at once).
 * Comments (and the whole panel in Proof/Annotate mode) is the review panel
 * (app-review-panel-v2), which reads comments for the entire template from the
 * review store.
 */
@Component({
  selector: 'app-edit-block-comments-v2',
  imports: [CommonModule, BlockRendererV2Component, ReviewPanelV2Component, SubjectComponentV2, PreheaderComponentV2],
  templateUrl: './edit-block-comments-v2.component.html',
  styleUrl: './edit-block-comments-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBlockCommentsV2Component implements OnChanges {
  @Input() selectedBlock: any | null = null;
  @Input() selectedBlockId: string | null = null;
  @Input() allBlocks: any[] = [];
  /** Block types with a dedicated always-visible v2 form (e.g. Main Banner) -
   *  every instance gets its own permanent section, same treatment as
   *  Subject/Preheader, instead of the master-detail "one at a time" section. */
  @Input() alwaysVisibleBlocks: any[] = [];
  @Input() activeTab: 'properties' | 'comments' = 'properties';
  @Input() blockCatalog: IBlockForSelect[] = [];
  /** Proof/Annotate replace both tabs with the full review panel. */
  @Input() reviewMode: 'off' | 'proof' | 'annotate' = 'off';
  @Input() currentUsername: string | null = null;
  @Input() languageLabel = 'EN';
  /** Restoring an older version needs the template lock. */
  @Input() canRestore = false;
  /** Shown as the Comments tab badge. */
  @Input() commentCount = 0;
  /** Disables the popout WYSIWYG editor button (Subject/Preheader/Main
   *  Banner) while the template isn't locked for editing. */
  @Input() fieldsDisabled = false;

  @Output() closeSelected = new EventEmitter<void>();
  @Output() setTab = new EventEmitter<'properties' | 'comments'>();
  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<any>();
  @Output() jumpToBlock = new EventEmitter<string>();
  /** Fired when the user focuses into the Subject/Preheader input, so the
   *  matching sidebar row and preview summary can highlight in sync. */
  @Output() focusBlock = new EventEmitter<string>();
  @Output() newVersion = new EventEmitter<void>();
  @Output() restoreVersion = new EventEmitter<number>();

  @ViewChild('rightPanel') private rightPanelRef!: ElementRef<HTMLElement>;

  private static readonly WIDTH_KEY = 'cr-right-panel-width-v2';
  private static readonly MIN_WIDTH = 320;
  private static readonly MAX_WIDTH = 640;

  /** Persisted per-user (AC3, RCA-390) - read once here, written on drag end. */
  protected panelWidth = Number(localStorage.getItem(EditBlockCommentsV2Component.WIDTH_KEY)) || 380;

  /** Drags the divider directly via the DOM (no change detection during the
   *  move) for a jank-free resize, then syncs `panelWidth` for future renders. */
  protected startResize(event: MouseEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.panelWidth;
    const el = this.rightPanelRef.nativeElement;

    const onMove = (moveEvent: MouseEvent) => {
      const width = Math.min(
        EditBlockCommentsV2Component.MAX_WIDTH,
        Math.max(EditBlockCommentsV2Component.MIN_WIDTH, startWidth + (startX - moveEvent.clientX))
      );
      this.panelWidth = width;
      el.style.width = `${width}px`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      localStorage.setItem(EditBlockCommentsV2Component.WIDTH_KEY, String(this.panelWidth));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedBlockId'] && this.selectedBlockId) {
      this.scrollSelectedBlockIntoView(this.selectedBlockId);
    }
  }

  /** Centers the selected block's own section within the scrollable right
   *  panel body whenever the selection changes (sidebar click, canvas click,
   *  Subject/Preheader focus, ...) - deferred a frame so a master-detail
   *  block's *ngIf="isOtherBlockSelected" section (not yet in the DOM on the
   *  same tick the input changes) has actually rendered by the time this runs. */
  private scrollSelectedBlockIntoView(blockId: string): void {
    requestAnimationFrame(() => {
      if (this.selectedBlockId !== blockId) {
        return;
      }
      const target = this.rightPanelRef?.nativeElement.querySelector(
        `[data-block-id="${CSS.escape(blockId)}"]`
      );
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  displayName(block: any): string {
    const catalogEntry = this.blockCatalog.find((c) => c.name === block?.name);
    return catalogEntry?.dropdownName || block?.name || '';
  }

  /** Subject/Preheader are always-on system fields, edited here permanently
   *  regardless of sidebar selection - see preview-v2 for their read-only summary. */
  get subjectBlock(): any {
    return this.allBlocks.find((b) => b?.name === 'subject');
  }

  get preheaderBlock(): any {
    return this.allBlocks.find((b) => b?.name === 'preheader');
  }

  get isOtherBlockSelected(): boolean {
    return (
      !!this.selectedBlock &&
      this.selectedBlock.name !== 'subject' &&
      this.selectedBlock.name !== 'preheader' &&
      !this.alwaysVisibleBlocks.some((b) => b.blockId === this.selectedBlock.blockId)
    );
  }

  /** Sidebar-matching position (1-based) of a block within the full block list -
   *  used so the numbering shown here (Subject, Preheader, Main Banner, Main
   *  Banner, ...) always lines up with the sidebar's own numbering. */
  blockIndex(block: any): number {
    return this.allBlocks.indexOf(block) + 1;
  }
}
